/**
 * Client service for Portal Notifications (IVS-1040).
 *
 * Unlike Broadcasts — which persist history client-side in Firestore — the
 * portal notification lifecycle (draft → published → archived) is owned by
 * iv3api's `portal_notifications` tables. Every call goes through
 * `iv3Request`, which resolves the per-env base URL and attaches the
 * operator token (with one 401/429 refresh-and-retry).
 *
 * Failed calls throw and are surfaced inline; nothing is persisted
 * client-side for this feature.
 */
import type {
  BroadcastEnv,
  CompanySearchResponse,
  CompanySearchResult,
  ListPortalNotificationsResponse,
  PortalNotification,
  PortalNotificationFormState,
  PortalNotificationInputBody,
  PortalNotificationStatus,
} from "src/types/portalNotifications";
import {
  PORTAL_CATEGORY_TO_PROTO,
  PORTAL_STATUS_TO_PROTO,
  PORTAL_TARGETING_TO_PROTO,
  portalCategoryFromProto,
  portalTargetingFromProto,
} from "src/types/portalNotifications";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
} from "./activityService";
import { iv3Request } from "./iv3Client";
import { getBroadcastEnvInfo } from "./notificationRoutes";

const ADMIN_PATH = "/api/v2/admin/portal-notifications";
const COMPANY_SEARCH_PATH = "/api/v2/companies/operator/search";
const COMPANY_BY_ID_PATH = (id: string) =>
  `/api/v2/operator/company/${encodeURIComponent(id)}`;

/** Operator-token request options shared by every admin call. */
const OPERATOR_AUTH = {
  auth: true,
  authHeader: "X_IVUEIT_AUTH_TOKEN",
  authScheme: "raw",
  baseUrl: (env: BroadcastEnv) => getBroadcastEnvInfo(env).baseUrl,
} as const;

// ---------------------------------------------------------------------------
// Form ↔ API mapping + validation
// ---------------------------------------------------------------------------

/** int64 fields arrive as JSON strings — coerce for display math. */
export function toMillis(v: string | number | undefined | null): number {
  if (v == null) return 0;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Convert API unix millis → `datetime-local` string.
 * Treats 0 / unset / invalid as empty so drafts without a window don't
 * show the epoch (e.g. "01-01-1970 05:30" in IST).
 */
export function millisToLocalDatetime(
  v: string | number | undefined | null,
): string {
  const ms = toMillis(v);
  if (ms <= 0) return "";
  const d = new Date(ms);
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Empty / invalid / epoch-or-earlier local strings → "" for the picker. */
export function sanitizeLocalDatetime(v: string | undefined | null): string {
  if (!v) return "";
  const ms = new Date(v).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return "";
  return v;
}

/** Local datetime → unix millis; empty/invalid → 0 (API = unset). */
function localDatetimeToMillis(v: string): number {
  if (!v) return 0;
  const ms = new Date(v).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return ms;
}

/** Build the PortalNotificationInput body from composer state. */
export function buildPortalNotificationInput(
  form: PortalNotificationFormState,
): PortalNotificationInputBody {
  return {
    headline: form.headline.trim(),
    body: form.body.trim(),
    ctaLabel: form.ctaLabel.trim(),
    ctaUrl: form.ctaUrl.trim(),
    category: PORTAL_CATEGORY_TO_PROTO[form.category],
    targeting: PORTAL_TARGETING_TO_PROTO[form.targeting],
    targetCompanyIds: form.targetCompanies.map((c) => c.id),
    startAtUnixMillis: localDatetimeToMillis(form.startAtLocal),
    endAtUnixMillis: localDatetimeToMillis(form.endAtLocal),
  };
}

/** Per-field validation messages for inline composer errors. */
export interface PortalNotificationFieldErrors {
  headline?: string;
  body?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  startAtLocal?: string;
  endAtLocal?: string;
  targeting?: string;
}

/** Collects all field errors (does not short-circuit). */
export function getPortalNotificationFieldErrors(
  form: PortalNotificationFormState,
): PortalNotificationFieldErrors {
  const errors: PortalNotificationFieldErrors = {};

  if (!form.headline.trim()) {
    errors.headline = "Headline is required.";
  }
  if (!form.body.trim()) {
    errors.body = "Body is required.";
  }

  const hasLabel = form.ctaLabel.trim() !== "";
  const hasUrl = form.ctaUrl.trim() !== "";
  if (hasLabel !== hasUrl) {
    const msg = "CTA label and URL must be provided together — or neither.";
    if (!hasLabel) errors.ctaLabel = msg;
    if (!hasUrl) errors.ctaUrl = msg;
  } else if (hasUrl) {
    try {
      const u = new URL(form.ctaUrl.trim());
      if (u.protocol !== "http:" && u.protocol !== "https:") {
        errors.ctaUrl = "CTA URL must start with http(s)://";
      }
    } catch {
      errors.ctaUrl = "CTA URL is not valid.";
    }
  }

  const start = new Date(form.startAtLocal).getTime();
  const end = new Date(form.endAtLocal).getTime();
  // datetime-local is minute precision — floor "now" so a start of "this
  // minute" (e.g. nowLocal()) is not treated as already past.
  const nowMinute = Math.floor(Date.now() / 60_000) * 60_000;

  if (!form.startAtLocal || Number.isNaN(start)) {
    errors.startAtLocal = "Start date/time is required.";
  } else if (start < nowMinute) {
    errors.startAtLocal = "Start date/time must be in the future.";
  }

  if (!form.endAtLocal || Number.isNaN(end)) {
    errors.endAtLocal = "End date/time is required.";
  } else if (!errors.startAtLocal && end <= start) {
    errors.endAtLocal = "End date/time must be after start date/time.";
  } else if (end < nowMinute) {
    errors.endAtLocal = "End date/time must be in the future.";
  }

  if (
    form.targeting === "SPECIFIC_COMPANIES" &&
    form.targetCompanies.length === 0
  ) {
    errors.targeting =
      "Pick at least one portal — or switch targeting to All portals.";
  }

  return errors;
}

/** Draft save: headline, body, and a started CTA are required. The window
 * may be empty, partial, or in the past. When both ends are set, end must
 * be after start — the same rule the API applies to a draft. "Must be in
 * the future" stays on publish (`getPortalNotificationFieldErrors`).
 * Targeting companies are optional until publish. */
export function getPortalNotificationDraftFieldErrors(
  form: PortalNotificationFormState,
): PortalNotificationFieldErrors {
  const errors = getPortalNotificationFieldErrors(form);
  delete errors.targeting;
  delete errors.startAtLocal;
  delete errors.endAtLocal;

  const startEmpty = !form.startAtLocal?.trim();
  const endEmpty = !form.endAtLocal?.trim();
  if (!startEmpty && !endEmpty) {
    const start = new Date(form.startAtLocal).getTime();
    const end = new Date(form.endAtLocal).getTime();
    if (!Number.isNaN(start) && !Number.isNaN(end) && end <= start) {
      errors.endAtLocal = "End date/time must be after start date/time.";
    }
  }
  return errors;
}

/** Banner copy for the composer actions. Empty when the form is valid. */
export function summarizePortalNotificationErrors(
  errors: PortalNotificationFieldErrors,
): string | null {
  const messages = [
    errors.headline,
    errors.body,
    errors.ctaLabel,
    errors.ctaUrl,
    errors.startAtLocal,
    errors.endAtLocal,
    errors.targeting,
  ].filter((message): message is string => Boolean(message));
  const unique = messages.filter(
    (message, index) => messages.indexOf(message) === index,
  );
  return unique.length > 0 ? unique.join("\n") : null;
}

/** Map an existing row back to composer form state for edit/duplicate. */
export function notificationToFormPrefill(
  n: PortalNotification,
): Partial<PortalNotificationFormState> {
  return {
    category: portalCategoryFromProto(n.category),
    headline: n.headline,
    body: n.body,
    ctaLabel: n.ctaLabel,
    ctaUrl: n.ctaUrl,
    startAtLocal: millisToLocalDatetime(n.startAtUnixMillis),
    endAtLocal: millisToLocalDatetime(n.endAtUnixMillis),
    targeting: portalTargetingFromProto(n.targeting),
    // API stores ids only — compose panel resolves display names on open.
    targetCompanies: n.targetCompanyIds.map((id) => ({ id, name: id })),
  };
}

// ---------------------------------------------------------------------------
// Admin API calls
// ---------------------------------------------------------------------------

export async function createPortalNotification(
  env: BroadcastEnv,
  form: PortalNotificationFormState,
  actor: ActivityActor | null,
): Promise<PortalNotification> {
  const created = await iv3Request<PortalNotification>(env, ADMIN_PATH, {
    ...OPERATOR_AUTH,
    method: "POST",
    body: JSON.stringify(buildPortalNotificationInput(form)),
  });
  await logActivity(actor, "create", created.id, form.headline, env);
  return created;
}

export async function updatePortalNotification(
  env: BroadcastEnv,
  id: string,
  form: PortalNotificationFormState,
  actor: ActivityActor | null,
): Promise<PortalNotification> {
  const updated = await iv3Request<PortalNotification>(
    env,
    `${ADMIN_PATH}/${id}`,
    {
      ...OPERATOR_AUTH,
      method: "PUT",
      body: JSON.stringify(buildPortalNotificationInput(form)),
    },
  );
  await logActivity(actor, "update", id, form.headline, env);
  return updated;
}

export async function listPortalNotifications(
  env: BroadcastEnv,
  statusFilter: PortalNotificationStatus | null,
  pageNumber: number,
  pageSize = 20,
): Promise<ListPortalNotificationsResponse> {
  const params = new URLSearchParams({
    pageSize: String(pageSize),
    pageNumber: String(pageNumber),
  });
  if (statusFilter) {
    params.set("statusFilter", PORTAL_STATUS_TO_PROTO[statusFilter]);
  }
  return iv3Request<ListPortalNotificationsResponse>(
    env,
    `${ADMIN_PATH}?${params.toString()}`,
    { ...OPERATOR_AUTH, method: "GET" },
  );
}

export async function publishPortalNotification(
  env: BroadcastEnv,
  id: string,
  headline: string,
  actor: ActivityActor | null,
): Promise<PortalNotification> {
  const res = await iv3Request<PortalNotification>(
    env,
    `${ADMIN_PATH}/${id}/publish`,
    {
      ...OPERATOR_AUTH,
      method: "POST",
      body: JSON.stringify({ id }),
    },
  );
  await logActivity(actor, "publish", id, headline, env);
  return res;
}

export async function archivePortalNotification(
  env: BroadcastEnv,
  id: string,
  headline: string,
  actor: ActivityActor | null,
): Promise<PortalNotification> {
  const res = await iv3Request<PortalNotification>(
    env,
    `${ADMIN_PATH}/${id}/archive`,
    {
      ...OPERATOR_AUTH,
      method: "POST",
      body: JSON.stringify({ id }),
    },
  );
  await logActivity(actor, "archive", id, headline, env);
  return res;
}

/** Company search for the targeting picker (operator-only endpoint). */
export async function searchCompanies(
  env: BroadcastEnv,
  query: string,
  pageSize = 20,
): Promise<CompanySearchResult[]> {
  const params = new URLSearchParams({
    query,
    "page.pageSize": String(pageSize),
    "page.pageNumber": "1",
  });
  const res = await iv3Request<CompanySearchResponse>(
    env,
    `${COMPANY_SEARCH_PATH}?${params.toString()}`,
    { ...OPERATOR_AUTH, method: "GET" },
  );
  return res.companies ?? [];
}

/** Fetch one company by id (operator portal). */
export async function getCompanyById(
  env: BroadcastEnv,
  companyId: string,
): Promise<CompanySearchResult> {
  const res = await iv3Request<CompanySearchResult>(
    env,
    COMPANY_BY_ID_PATH(companyId),
    { ...OPERATOR_AUTH, method: "GET" },
  );
  return {
    id: res.id,
    name: res.name || res.id,
    shortcode: res.shortcode,
  };
}

/**
 * Replace id-as-name chips (from draft prefill) with real company names.
 * Best-effort per id — failures keep the original entry.
 */
export async function resolveTargetCompanyNames(
  env: BroadcastEnv,
  companies: CompanySearchResult[],
): Promise<CompanySearchResult[]> {
  const needsResolve = companies.some((c) => !c.name || c.name === c.id);
  if (!needsResolve) return companies;

  return Promise.all(
    companies.map(async (c) => {
      if (c.name && c.name !== c.id) return c;
      try {
        return await getCompanyById(env, c.id);
      } catch (e) {
        console.warn("Failed to resolve company name for", c.id, e);
        return c;
      }
    }),
  );
}

/** Best-effort audit entry — never fails the API call it follows. */
async function logActivity(
  actor: ActivityActor | null,
  action: string,
  id: string,
  headline: string,
  env: BroadcastEnv,
): Promise<void> {
  if (!actor) return;
  try {
    await appendActivity({
      domain: ActivityDomains.PortalNotifications,
      action,
      resourceType: "portal_notification",
      resourceId: id,
      summary: `${action === "create" ? "Created" : action === "update" ? "Updated" : action === "publish" ? "Published" : "Archived"} portal notification: "${headline || "(untitled)"}"`,
      actor,
      details: { env },
    });
  } catch (e) {
    console.error("Failed to log portal notification activity:", e);
  }
}
